import { IntervalTree } from '@fullstory/interval-tree';
import { AppendOnlyArray, IterableList } from '@fullstory/util/collections';
import { sortBy } from 'lodash';
import { skip } from 'rxjs';
import { PageModel } from './Page';

export class PageList extends AppendOnlyArray<PageModel> {
  private _pageIdsByTime = new IntervalTree<string>();
  private _pageIndicesById = new Map<string, number>();

  private _hasPage(page: Readonly<PageModel>): boolean {
    return this._pageIndicesById.has(page.pageId);
  }

  /**
   * Save a page's duration for future lookup.
   * TODO(jordanhurt) - investigate performance implications of continuously updating
   * an IntervalTree as the duration is increased. It may be more performant to simply
   * iterate through a list of pages for those that have a duration within bounds.
   */
  private _savePageDuration(page: Readonly<PageModel>) {
    const { duration, pageId } = page;
    const startTime = this._getStartTime(page);

    this._pageIdsByTime.insert({
      high: startTime + duration,
      low: startTime,
      value: pageId,
    });
  }

  private _savePageIndex(page: Readonly<PageModel>, idx: number) {
    this._pageIndicesById.set(page.pageId, idx);
  }

  // TODO(jordanhurt) - this method is a hack for LinearizedTabModel
  // remove it when we remove LinearizedTabModel
  protected _getStartTime(page: Readonly<PageModel>): number {
    return page.localStartTime;
  }

  constructor(initializer?: PageModel[]) {
    super();

    // NOTE: this method _can't_ call super directly because the super constructor
    // calls `append()` before the `_timeline` member can be initialized.
    if (initializer) {
      this.append(initializer);
    }
  }

  /**
   * PageListModel overrides AppendOnlyArray's `append` method
   * to merge and update duplicate pages
   */
  append(sequence: IterableList<PageModel>) {
    const nextBatch: PageModel[] = [];
    const startIdx = this.length;
    const sortedPages = sortBy([...sequence], page => this._getStartTime(page));

    for (const page of sortedPages) {
      const nextIdx = startIdx + nextBatch.length;

      if (!this._hasPage(page)) {
        nextBatch.push(page as PageModel);

        this._savePageDuration(page);
        this._savePageIndex(page, nextIdx);

        // Update the page's duration whenever it receives new event data
        // NOTE: duration$ emits immediately because it is a BehaviorSubject,
        // so we need to skip the first value.
        page.duration$.pipe(skip(1)).subscribe(() => {
          this._savePageDuration(page);
        });
      }
    }

    super.append(nextBatch);
  }

  at(offsetMs: number): Readonly<PageModel>[] {
    // NOTE: Set prevents duplicates
    // [MDN: Set](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set)
    const uniqueIds = new Set(this._pageIdsByTime.at(offsetMs));
    const results: PageModel[] = [];

    for (const id of uniqueIds) {
      const page = this.byId(id);

      if (page) {
        results.push(page as PageModel);
      }
    }

    return [...results];
  }

  atOrBefore(offsetMs: number): Readonly<PageModel>[] {
    const uniqueIds = new Set(this._pageIdsByTime.atOrBefore(offsetMs));
    const results: PageModel[] = [];

    for (const id of uniqueIds) {
      const page = this.byId(id);

      if (page) {
        results.push(page as PageModel);
      }
    }

    return [...results];
  }

  byId(id: string): Readonly<PageModel> | undefined {
    return this.get(this.indexForId(id));
  }

  byKey(key: string): Readonly<PageModel> | undefined {
    for (const page of this) {
      if (key === page.key) {
        return page;
      }
    }

    return undefined;
  }

  indexForId(id: string): number {
    return this._pageIndicesById.get(id) ?? -1;
  }

  get startTime(): number {
    return this._pageIdsByTime.min;
  }

  get endTime(): number {
    return this._pageIdsByTime.max;
  }
}

/**
 * @deprecated
 */
export class LinearizedPageList extends PageList {
  protected _getStartTime(page: Readonly<PageModel>): number {
    return page.startTime;
  }
}
